import {
  Card,
  CardHeader,
  Title3,
  Body1,
  Caption1,
  Switch,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Field,
  Input,
  tokens,
} from '@fluentui/react-components';
import { Translate24Regular, Person24Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { SettingsAuthSection } from './SettingsAuthSection';
import { useAuth } from '../auth/AuthContext';
import { authApi } from '../auth/authApi';
import {
  scanScopeApi,
  SCAN_SCOPE_PRODUCTS,
  type ScanScopeProduct,
  type AvailableProduct,
} from '../api/scanScopeApi';
import { onboardingApi } from '../api/onboardingApi';
import { apiFetch } from '../api/apiClient';
import { FlagIcon } from '../components/FlagIcon';

export function SettingsPage() {
  const { t } = useTranslation();
  return (
    <div>
      <PageHeader
        title={t('settings.title')}
        description={t('settings.subtitleFull')}
      />

      {/* Profil utilisateur (Mon compte) */}
      <ProfileSection />

      {/* Section auth (definir/modifier mot de passe) */}
      <div style={{ marginTop: 16 }}>
        <SettingsAuthSection />
      </div>

      {/* Section langue des notifications email (alertes, digests) */}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <NotificationLanguageSection />
      </div>

      {/* Périmètre du scan -> déplacé dans Organisation › Paramètres Microsoft 365.
          À propos -> déplacé dans Organisation › Avancés. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileSection - "Mon profil" : nom/prénom/téléphone éditables ;
// entreprise, type de compte et e-mail (identifiant) verrouillés.
// ---------------------------------------------------------------------------
function ProfileSection() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();

  const [company, setCompany]     = useState('');
  const [bce, setBce]             = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [phone, setPhone]         = useState('');
  const [snap, setSnap]           = useState({ firstName: '', lastName: '', phone: '' });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [success, setSuccess]     = useState(false);

  useEffect(() => {
    if (!user) return;
    const parts = (user.name ?? '').trim().split(/\s+/).filter(Boolean);
    const fn = user.firstName ?? parts[0] ?? '';
    const ln = user.lastName ?? (parts.length > 1 ? parts.slice(1).join(' ') : '');
    setFirstName(fn);
    setLastName(ln);
    setPhone(user.phone ?? '');
    setSnap({ firstName: fn, lastName: ln, phone: user.phone ?? '' });
  }, [user]);

  useEffect(() => {
    onboardingApi.status()
      .then((s) => {
        setCompany(s.companyLegalName ?? s.companyName ?? s.organizationName ?? '');
        setBce(s.companyBce ?? '');
      })
      .catch(() => {});
  }, []);

  const dirty = firstName !== snap.firstName || lastName !== snap.lastName || phone !== snap.phone;
  const roleLabel =
    user?.role === 'admin'   ? t('settings.profile.roleAdmin', 'Administrateur') :
    user?.role === 'auditor' ? t('settings.profile.roleAuditor', 'Auditeur') :
    user?.role === 'viewer'  ? t('settings.profile.roleViewer', 'Lecteur') :
    (user?.role ?? '—');

  async function save() {
    setSaving(true); setError(null); setSuccess(false);
    try {
      await authApi.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() });
      setSnap({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() });
      await refresh();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader
        image={<Person24Regular primaryFill={tokens.colorBrandForeground1} />}
        header={<Title3>{t('settings.profile.title', 'Mon profil')}</Title3>}
      />
      {error   && <MessageBar intent="error"   style={{ marginBottom: 12 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {success && <MessageBar intent="success" style={{ marginBottom: 12 }}><MessageBarBody>{t('settings.profile.saved', 'Profil enregistré.')}</MessageBarBody></MessageBar>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
        <Field label={t('settings.profile.firstName', 'Prénom')}>
          <Input value={firstName} onChange={(_, d) => setFirstName(d.value)} />
        </Field>
        <Field label={t('settings.profile.lastName', 'Nom')}>
          <Input value={lastName} onChange={(_, d) => setLastName(d.value)} />
        </Field>
        <Field label={t('settings.profile.company', 'Entreprise')} hint={t('settings.profile.lockedHint', 'Non modifiable')}>
          <Input value={company} readOnly disabled />
        </Field>
        <Field label={t('settings.profile.bce', "N° d'entreprise (BCE)")} hint={t('settings.profile.lockedHint', 'Non modifiable')}>
          <Input value={bce} readOnly disabled placeholder="—" />
        </Field>
        <Field label={t('settings.profile.accountType', 'Type de compte')} hint={t('settings.profile.lockedHint', 'Non modifiable')}>
          <Input value={roleLabel} readOnly disabled />
        </Field>
        <Field label={t('settings.profile.phone', 'Numéro de téléphone')}>
          <Input value={phone} type="tel" placeholder="+32 ..." onChange={(_, d) => setPhone(d.value)} />
        </Field>
        <Field label={t('settings.profile.email', 'E-mail (identifiant de connexion)')} hint={t('settings.profile.lockedHint', 'Non modifiable')}>
          <Input value={user?.email ?? ''} readOnly disabled />
        </Field>
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button appearance="primary" onClick={() => void save()} disabled={!dirty || saving} icon={saving ? <Spinner size="tiny" /> : undefined}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ScanScopeSection - permet de desactiver des produits M365 du scan.
// Lecture libre (tous roles), edition admin + auditor.
// ---------------------------------------------------------------------------
const PRODUCT_LABEL: Record<ScanScopeProduct, string> = {
  intune:        'Microsoft Intune',
  exchange:      'Exchange Online',
  purview:       'Microsoft Purview',
  teams:         'Microsoft Teams',
  powerPlatform: 'Power Platform',
  defenderXdr:   'Defender XDR',
  mcas:          'Defender for Cloud Apps (MCAS)',
};

const PRODUCT_HINT: Record<ScanScopeProduct, string> = {
  intune:        'Endpoints, BitLocker via Intune, devices compliance.',
  exchange:      'Anti-phishing, DKIM, SPF, DMARC, alert policies.',
  purview:       'Sensitivity labels, retention, DLP, IRM, Records Management.',
  teams:         'Federation, anonymous join, app policies, external collab.',
  powerPlatform: 'Power Apps, Power Automate, DLP connectors.',
  defenderXdr:   'Advanced Hunting, XDR integrations, risky sign-ins.',
  mcas:          'Shadow IT, OAuth apps governance, file policies MCAS.',
};

export function ScanScopeSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'auditor';

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [available, setAvailable] = useState<AvailableProduct[]>([]);
  const [excluded, setExcluded] = useState<Set<ScanScopeProduct>>(new Set());
  const [initial, setInitial]   = useState<Set<ScanScopeProduct>>(new Set());
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await scanScopeApi.get();
      setAvailable(data.availableProducts);
      const set = new Set<ScanScopeProduct>(data.excludedProducts);
      setExcluded(set);
      setInitial(new Set(set));
      setUpdatedAt(data.updatedAt);
      setUpdatedBy(data.updatedBy);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const dirty = useMemo(() => {
    if (excluded.size !== initial.size) return true;
    for (const p of excluded) if (!initial.has(p)) return true;
    return false;
  }, [excluded, initial]);

  const totalControlsExcluded = useMemo(() => {
    let sum = 0;
    for (const p of excluded) {
      const a = available.find((x) => x.code === p);
      if (a) sum += a.controlsCount;
    }
    return sum;
  }, [excluded, available]);

  function toggle(code: ScanScopeProduct, checked: boolean) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(code);   // checked = produit ACTIVE = NON exclu
      else         next.add(code);      // unchecked = exclu
      return next;
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await scanScopeApi.update(Array.from(excluded));
      setInitial(new Set(res.excludedProducts));
      setUpdatedAt(res.updatedAt);
      setUpdatedBy(res.updatedBy);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader header={<Title3>{t('settings.scope.title')}</Title3>} />
      <Body1 style={{ display: 'block', marginBottom: 12 }}>
        {t('settings.scope.subtitle')}
      </Body1>

      {loading && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spinner size="small" />
        </div>
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!canEdit && !loading && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>{t('settings.scope.readOnlyHint')}</MessageBarBody>
        </MessageBar>
      )}

      {!loading && (
        <>
          <div
            style={{
              display:        'flex',
              flexDirection:  'column',
              gap:            8,
              marginBottom:   16,
            }}
          >
            {SCAN_SCOPE_PRODUCTS.map((code) => {
              const isExcluded = excluded.has(code);
              const isEnabled  = !isExcluded;
              const meta       = available.find((a) => a.code === code);
              return (
                <div
                  key={code}
                  style={{
                    display:        'flex',
                    alignItems:     'center',
                    gap:            12,
                    padding:        '10px 4px',
                    borderTop:      `1px solid ${tokens.colorNeutralStroke2}`,
                    opacity:        canEdit ? 1 : 0.7,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Body1 style={{ fontWeight: 600, display: 'block' }}>
                      {PRODUCT_LABEL[code]}
                      {meta && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize:   11,
                            fontWeight: 500,
                            color:      tokens.colorNeutralForeground3,
                          }}
                        >
                          {t('settings.scope.controlsCount', { count: meta.controlsCount })}
                        </span>
                      )}
                    </Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {PRODUCT_HINT[code]}
                    </Caption1>
                  </div>
                  <Switch
                    checked={isEnabled}
                    disabled={!canEdit || saving}
                    onChange={(_, d) => toggle(code, d.checked)}
                  />
                </div>
              );
            })}
          </div>

          <div
            style={{
              display:         'flex',
              justifyContent:  'space-between',
              alignItems:      'center',
              gap:             12,
              flexWrap:        'wrap',
            }}
          >
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {excluded.size === 0
                ? t('settings.scope.summaryNoneExcluded')
                : t('settings.scope.summaryExcluded', {
                    products: excluded.size,
                    controls: totalControlsExcluded,
                  })}
              {updatedAt && (
                <span style={{ marginLeft: 12 }}>
                  · {t('settings.scope.lastUpdated', {
                       date: new Date(updatedAt).toLocaleDateString(),
                       user: updatedBy ?? '',
                     })}
                </span>
              )}
            </Caption1>
            {canEdit && (
              <Button
                appearance="primary"
                onClick={save}
                disabled={!dirty || saving}
                icon={saving ? <Spinner size="tiny" /> : undefined}
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// NotificationLanguageSection - choix de la langue par defaut des emails de
// notifications envoyes par le tenant (alertes, digests, magic links groupes).
// Distinct de la langue du user dans l UI : ici c est la langue ORG-LEVEL.
// ---------------------------------------------------------------------------
const NOTIF_LANGS = [
  { code: 'fr-BE', label: 'Français (Belgique)', flag: 'fr' as const },
  { code: 'nl-BE', label: 'Nederlands (België)', flag: 'nl' as const },
  { code: 'de-BE', label: 'Deutsch (Belgien)',   flag: 'de' as const },
  { code: 'en',    label: 'English',             flag: 'en' as const },
] as const;
type NotifLang = typeof NOTIF_LANGS[number]['code'];

function NotificationLanguageSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);
  const [current, setCurrent]   = useState<NotifLang>('fr-BE');
  const [initial, setInitial]   = useState<NotifLang>('fr-BE');

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const status = await onboardingApi.status();
      const lang   = (status?.notificationLanguage ?? 'fr-BE') as NotifLang;
      setCurrent(lang);
      setInitial(lang);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await apiFetch('/clients/customization', {
        method: 'POST',
        body:   JSON.stringify({ notificationLanguage: current }),
        headers: { 'Content-Type': 'application/json' },
      });
      setInitial(current);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const dirty = current !== initial;
  const currentMeta = NOTIF_LANGS.find((l) => l.code === current) ?? NOTIF_LANGS[0];

  return (
    <Card>
      <CardHeader
        image={<Translate24Regular primaryFill={tokens.colorBrandForeground1} />}
        header={<Title3>{t('settings.notifLang.title')}</Title3>}
      />
      <Body1 style={{ display: 'block', marginBottom: 12 }}>
        {t('settings.notifLang.subtitle')}
      </Body1>

      {loading && <Spinner size="small" />}
      {!loading && error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {!loading && success && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{t('settings.notifLang.saved')}</MessageBarBody>
        </MessageBar>
      )}
      {!loading && !canEdit && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>{t('settings.notifLang.readOnlyHint')}</MessageBarBody>
        </MessageBar>
      )}

      {!loading && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <Field label={t('settings.notifLang.label')} hint={t('settings.notifLang.hint')} style={{ flex: 1, minWidth: 280 }}>
            {/* v2026.06.115 - FlagIcon adjacent toujours visible (Fluent Dropdown
                n affiche pas les children dans le bouton ferme). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FlagIcon code={currentMeta.flag} size={20} />
              <Dropdown
                style={{ flex: 1 }}
                value={currentMeta.label}
                selectedOptions={[current]}
                disabled={!canEdit || saving}
                onOptionSelect={(_, d) => setCurrent((d.optionValue as NotifLang) ?? 'fr-BE')}
              >
                {NOTIF_LANGS.map((l) => (
                  <Option key={l.code} value={l.code} text={l.label}>
                    <FlagIcon code={l.flag} style={{ marginRight: 6 }} />
                    {l.label}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </Field>          {canEdit && (
            <Button
              appearance="primary"
              onClick={() => { void save(); }}
              disabled={saving || current === initial}
            >
              {saving ? t('common.saving', { defaultValue: 'Enregistrement...' }) : t('common.save', { defaultValue: 'Enregistrer' })}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
